import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Package, Search } from 'lucide-react';
import SiteHeader from '../components/SiteHeader';
import SiteFooter from '../components/SiteFooter';
import { LOJA_PAGE } from '../content/siteCopy';
import { trackLojaSearch, trackPublicEvent } from '../lib/publicAnalytics';
import {
  destaqueParaVitrine,
  getItensLojaPublica,
  imagemProdutoVitrineRow,
} from '../data/catalogoDestaque';

type Row = {
  id: string;
  nome: string;
  descricao?: string;
  preco_venda: number;
  categoria?: string;
};

function formatMoney(n: number) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(n);
}

const STORE_ROWS: Row[] = getItensLojaPublica().map(destaqueParaVitrine);

export default function Loja() {
  const [q, setQ] = useState('');

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return STORE_ROWS;
    return STORE_ROWS.filter(
      (r) =>
        r.nome.toLowerCase().includes(t) ||
        (r.categoria && String(r.categoria).toLowerCase().includes(t)) ||
        (r.descricao && r.descricao.toLowerCase().includes(t))
    );
  }, [q]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      trackLojaSearch(q);
    }, 700);
    return () => window.clearTimeout(timer);
  }, [q]);

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />

      <main className="flex-1">
        <div className="border-b border-lunins-border bg-hero-mesh">
          <div className="mx-auto max-w-6xl px-4 py-14 md:py-20">
            <p className="text-xs font-semibold uppercase tracking-widest text-lunins-gold">Loja oficial</p>
            <h1 className="mt-2 font-display text-3xl font-semibold text-lunins-ink md:text-4xl">{LOJA_PAGE.title}</h1>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-lunins-muted md:text-base">{LOJA_PAGE.subtitle}</p>
            <div className="relative mt-8 max-w-md">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-lunins-muted" />
              <input
                type="search"
                placeholder="Buscar por nome ou categoria…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="w-full rounded-xl border border-lunins-border bg-lunins-surface py-3 pl-11 pr-4 text-sm text-lunins-ink placeholder:text-lunins-muted focus:border-lunins-gold/60 focus:outline-none focus:ring-2 focus:ring-lunins-gold/20"
              />
            </div>
          </div>
        </div>

        <div className="mx-auto max-w-6xl px-4 py-14 md:py-20">
          {filtered.length === 0 && (
            <p className="text-center text-lunins-muted">Nenhum produto encontrado.</p>
          )}

          {filtered.length > 0 && (
            <ul className="grid gap-7 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((p) => (
                <li key={p.id}>
                  <Link
                    to={`/loja/${p.id}`}
                    onClick={() => trackPublicEvent('produto_click', { origem: 'loja_grid', produto_id: p.id, produto_nome: p.nome })}
                    className="lunins-card group flex h-full flex-col overflow-hidden transition hover:border-lunins-gold/50"
                  >
                    <div className="relative flex aspect-[4/3] items-center justify-center overflow-hidden bg-lunins-elevated">
                      {(() => {
                        const img = imagemProdutoVitrineRow({ id: p.id, nome: p.nome });
                        return img ? (
                          <img
                            src={img}
                            alt={p.nome}
                            className="relative h-full w-full object-contain p-3 transition duration-300 group-hover:scale-[1.02]"
                            loading="lazy"
                          />
                        ) : (
                          <Package className="relative h-14 w-14 text-lunins-gold/80 transition group-hover:scale-105" strokeWidth={1.25} />
                        );
                      })()}
                    </div>
                    <div className="flex flex-1 flex-col p-5">
                      {p.categoria && (
                        <span className="mb-2 inline-flex w-fit rounded-md bg-lunins-sand px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-lunins-muted">
                          {String(p.categoria)}
                        </span>
                      )}
                      <h2 className="text-lg font-semibold leading-snug tracking-normal text-lunins-ink group-hover:text-lunins-teal">
                        {p.nome}
                      </h2>
                      {p.descricao && (
                        <p className="mt-2 line-clamp-2 text-sm text-lunins-muted">{p.descricao}</p>
                      )}
                      <p className="mt-auto pt-4 font-display text-xl font-semibold text-lunins-ink">{formatMoney(Number(p.preco_venda))}</p>
                      <span className="mt-3 text-xs font-semibold text-lunins-teal group-hover:underline">
                        Ver detalhes →
                      </span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
